import type { Pill } from "./stage.js";

/**
 * Where the build is, as six pills across the top of the screen.
 *
 * The labels are `PHASE_LABELS` — the same six words the classic timeline uses.
 * Two skins over one pipeline should not name the same five minutes two
 * different ways, and of the two vocabularies this is the one that says what is
 * actually happening. The design's own names for these stages did not go away;
 * they moved to the status line beside the egg, where being charming costs the
 * reader nothing.
 *
 * One pill per `BuildPhase`, including `queued` — the classic timeline hides
 * that one as bookkeeping, which leaves nothing marked active for the whole of
 * the wait. Here the egg is already wobbling by then and the status line is
 * already talking, so a tracker showing no current step would be the only thing
 * on screen claiming the build had not started.
 *
 * The rules between pills are the classic timeline's, kept: they are what turns
 * six chips into a sequence with a direction. A pill on its own says "backend";
 * a pill with a line running out of it says "backend, then".
 *
 * The pills only ever advance, because the value behind them only ever
 * advances: the stream reducer refuses a backwards phase, so a timeline that
 * jumps back and reads as failure is impossible here by construction.
 */
export function StagePills({ pills }: { pills: Pill[] }) {
  return (
    <ol
      className="flex shrink-0 flex-wrap items-center justify-center gap-y-1 border-b-2 border-shell-rule bg-card/60 px-5 py-2.5 backdrop-blur-[8px]"
      aria-label="Build progress"
    >
      {pills.map((pill, i) => (
        <li
          key={pill.label}
          aria-current={pill.state === "active" || pill.state === "halted" ? "step" : undefined}
          className="flex items-center"
        >
          <span
            className={`flex items-center gap-1.5 rounded-full px-3 py-[5px] ${
              pill.state === "active" || pill.state === "halted" ? "bg-ember-soft" : ""
            }`}
          >
            <span
              aria-hidden
              className={`size-2 rounded-full ${DOT[pill.state]} ${
                pill.pulse ? "animate-[dotPulse_1s_ease-in-out_infinite]" : ""
              }`}
            />
            <span
              className={`text-[13px] ${
                pill.state === "pending"
                  ? "font-semibold text-bark-faint"
                  : pill.state === "done"
                    ? "font-semibold text-bark"
                    : "font-extrabold text-bark"
              }`}
            >
              {pill.label}
            </span>
          </span>
          {i < pills.length - 1 && (
            <span aria-hidden className="h-px w-3 bg-shell-rule sm:w-5" />
          )}
        </li>
      ))}
    </ol>
  );
}

/**
 * `halted` is the phase a stopped build died in.
 *
 * It reads as ink rather than as the accent, because the accent means "here,
 * and now" and a stopped build is neither. Sharing the accent made a halted
 * pill differ from an active one only by the missing pulse — a distinction
 * nobody catches on a still screen, and one that vanishes entirely under
 * reduced motion.
 */
const DOT: Record<Pill["state"], string> = {
  done: "bg-moss",
  active: "bg-ember",
  halted: "bg-bark",
  pending: "bg-pending",
};
